import type {
  AiOperationsCurrentProposal,
  ApprovalProposalAction,
  ApprovalProposalDetail,
  ApprovalRejectionReason,
} from "@adpilot/shared";
import { IconArrowRight, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ActionDecisionDraft } from "@/components/ai-operations/ai-run-decision";
import { ControlButton } from "@/components/patterns/control-button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { SegmentControl, SegmentControlItem } from "@/components/ui/segment-control";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { formatSignedCurrencyDiff, useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";
import { DecisionBadge, Fact } from "./DecisionRecordPrimitives";
import {
  riskTextClass,
  routeLabel,
  targetTitle,
  targetTypeLabel,
} from "./decision-record-presentation";

type DraftDecision = "approve" | "reject" | "modify_approve";

const rejectionReasonValues: ApprovalRejectionReason[] = [
  "strategic_keyword",
  "core_keyword_protection",
  "insufficient_data",
  "external_promotion",
  "inventory_reason",
  "wrong_interpretation",
  "manual_override",
  "other",
];

export function ActionDecisionSheet({
  action,
  canDecide,
  onOpenChange,
  onSubmit,
  open,
  review,
  reviewAction,
  submitError,
  submitting,
}: {
  action: AiOperationsCurrentProposal["artifact"]["actions"][number];
  canDecide: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (draft: ActionDecisionDraft) => void;
  open: boolean;
  review?: ApprovalProposalDetail;
  reviewAction?: ApprovalProposalAction;
  submitError: string | null;
  submitting: boolean;
}): React.ReactElement {
  const { t } = useTranslation(["aiOperations", "approval"]);
  const { formatCurrency } = useFormatters();
  const [decision, setDecision] = useState<DraftDecision>("approve");
  const [reasonCode, setReasonCode] = useState<ApprovalRejectionReason | null>(null);
  const [modifiedBid, setModifiedBid] = useState(action.server.newBid.toFixed(2));
  const target = review?.targets.find(({ id }) => id === action.target.id);
  const actionIndex = review?.actions.findIndex(({ id }) => id === action.id) ?? -1;
  const validModifiedBid = Number.isFinite(Number(modifiedBid)) && Number(modifiedBid) > 0;
  const rejectionReasons: Array<{ value: ApprovalRejectionReason | null; label: string }> = [
    { value: null, label: t("approval:reviewDialog.noReason") },
    ...rejectionReasonValues.map((value) => ({
      value,
      label: t(`approval:rejectReason.${value}`),
    })),
  ];
  const persisted = reviewAction !== undefined && reviewAction.reviewStatus !== "pending";
  const increase = action.server.changePercentage >= 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-[448px] max-w-[448px] gap-0 rounded-none data-[side=right]:sm:max-w-[448px]"
        showCloseButton={false}
      >
        <div className="flex min-h-[103px] shrink-0 items-start justify-between border-b border-line px-5 pb-4 pt-5">
          <div className="flex flex-col gap-[5px]">
            <p className="text-xs leading-4 text-muted-foreground">
              {t("aiOperations:runDetail.decisionRecord.sheet.position", {
                current: actionIndex >= 0 ? actionIndex + 1 : 1,
                total: review?.actions.length ?? 1,
              })}
            </p>
            <SheetTitle className="text-lg font-semibold leading-6 text-ink">
              {targetTitle(action, t)}
            </SheetTitle>
            <SheetDescription className="text-xs leading-4">
              {targetTypeLabel(action, target?.keywordMatchType, t)} ·{" "}
              {t(`aiOperations:runDetail.decisionRecord.risk.${action.server.riskLevel}`)}
            </SheetDescription>
          </div>
          <ControlButton
            controlSize="icon-compact"
            variant="outline"
            aria-label={t("aiOperations:runDetail.decisionRecord.sheet.close")}
            onClick={() => onOpenChange(false)}
          >
            <IconX />
          </ControlButton>
        </div>

        <div className="flex min-h-[72px] shrink-0 items-center justify-between border-b border-line px-5 py-4">
          <div>
            <p className="text-xs leading-4 text-muted-foreground">
              {t("aiOperations:runDetail.decisionRecord.sheet.currentBid")}
            </p>
            <p className="text-base font-semibold leading-[21px] text-ink">
              {formatCurrency(action.server.oldBid)}
            </p>
          </div>
          {/* 差值语义上描述"变化"本身,标注在箭头下方,两侧保持对称的
              标签+数值两行结构。 */}
          <div className="flex flex-col items-center gap-0.5">
            <IconArrowRight className="size-[18px] text-muted-foreground" />
            <span className={cn("text-xs leading-4", increase ? "text-green-11" : "text-red-11")}>
              {formatSignedCurrencyDiff(action.server.oldBid, action.server.newBid, formatCurrency)}
            </span>
          </div>
          <div className="text-right">
            <p className="text-xs leading-4 text-muted-foreground">
              {t("aiOperations:runDetail.decisionRecord.sheet.aiProposal")}
            </p>
            <p
              className={cn(
                "text-base font-semibold leading-[21px]",
                increase ? "text-green-11" : "text-red-11",
              )}
            >
              {formatCurrency(action.server.newBid)}
            </p>
          </div>
        </div>

        <div className="flex min-h-[145px] shrink-0 flex-col gap-[7px] border-b border-line px-5 py-[18px]">
          <p className="text-xs font-medium leading-4 text-muted-foreground">
            {t("aiOperations:runDetail.decisionRecord.sheet.aiReason")}
          </p>
          <p className="text-sm font-medium leading-[21px] text-ink">{action.agent.reasoning}</p>
          <p className="text-xs leading-[18px] text-muted-foreground">
            {t("aiOperations:runDetail.decisionRecord.sheet.policyExplanation")}
          </p>
        </div>

        <Accordion className="shrink-0 border-b border-line">
          <AccordionItem value="evidence" className="border-0 px-5">
            <AccordionTrigger className="min-h-[63px] py-3.5 hover:no-underline">
              <span className="flex items-center gap-[9px]">
                <span className="text-left">
                  <span
                    className={cn(
                      "block text-[13px] font-semibold leading-[18px]",
                      riskTextClass(action.server.riskLevel),
                    )}
                  >
                    {t(`aiOperations:runDetail.decisionRecord.risk.${action.server.riskLevel}`)}
                  </span>
                  <span className="block text-xs font-normal leading-4 text-muted-foreground">
                    {routeLabel(action.server.route, t)}
                  </span>
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <EvidenceDetails action={action} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-[18px]">
          {persisted ? (
            <div className="rounded-[10px] border border-line bg-subtle/40 p-4">
              <p className="text-xs font-medium text-muted-foreground">
                {t("aiOperations:runDetail.decisionRecord.sheet.recordedDecision")}
              </p>
              <div className="mt-2">
                <DecisionBadge status={reviewAction?.reviewStatus ?? "not_required"} />
              </div>
            </div>
          ) : canDecide ? (
            <div className="flex flex-col gap-3.5">
              <Field>
                <FieldLabel>
                  {t("aiOperations:runDetail.decisionRecord.sheet.yourDecision")}
                </FieldLabel>
                <SegmentControl
                  aria-label={t("aiOperations:runDetail.decisionRecord.sheet.yourDecision")}
                  value={decision}
                  widthMode="equal"
                  onValueChange={setDecision}
                >
                  <SegmentControlItem value="approve">
                    {t("approval:decision.approve")}
                  </SegmentControlItem>
                  <SegmentControlItem value="reject" className="data-pressed:text-red-11">
                    {t("approval:decision.reject")}
                  </SegmentControlItem>
                  <SegmentControlItem value="modify_approve">
                    {t("aiOperations:runDetail.decisionRecord.sheet.modifyBid")}
                  </SegmentControlItem>
                </SegmentControl>
              </Field>
              {decision === "reject" ? (
                <Field>
                  <FieldLabel>
                    {t("aiOperations:runDetail.decisionRecord.sheet.reasonOptional")}
                  </FieldLabel>
                  <Select
                    items={rejectionReasons}
                    value={reasonCode}
                    onValueChange={(value) =>
                      setReasonCode(value as ApprovalRejectionReason | null)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {rejectionReasons.map((reason) => (
                          <SelectItem key={reason.value ?? "none"} value={reason.value}>
                            {reason.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </Field>
              ) : null}
              {decision === "modify_approve" ? (
                <Field data-invalid={!validModifiedBid || undefined}>
                  <FieldLabel htmlFor={`modified-bid-${action.id}`}>
                    {t("aiOperations:runDetail.decisionRecord.sheet.approvedBid")}
                  </FieldLabel>
                  <Input
                    id={`modified-bid-${action.id}`}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={modifiedBid}
                    aria-invalid={!validModifiedBid}
                    onChange={(event) => setModifiedBid(event.target.value)}
                  />
                </Field>
              ) : null}
              <div className="flex items-start gap-2 rounded-[8px] bg-canvas px-[11px] py-2.5 text-xs leading-[18px] text-muted-foreground">
                <span className="mt-1.5 size-[5px] shrink-0 rounded-full bg-muted-foreground" />
                {t("aiOperations:runDetail.decisionRecord.sheet.reasonHint")}
              </div>
            </div>
          ) : (
            <p className="text-xs leading-5 text-muted-foreground">
              {t("aiOperations:runDetail.decision.noPermission")}
            </p>
          )}
        </div>

        <div className="flex min-h-[65px] shrink-0 items-center justify-between gap-2 border-t border-line px-5 py-3.5">
          <div className="min-w-0">
            {submitError ? <FieldError>{submitError}</FieldError> : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ControlButton
              controlSize="toolbar"
              variant="outline"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
            >
              {t("common:actions.cancel")}
            </ControlButton>
            {canDecide && !persisted ? (
              <ControlButton
                controlSize="toolbar"
                loading={submitting}
                disabled={submitting || (decision === "modify_approve" && !validModifiedBid)}
                onClick={() => onSubmit({ decision, reasonCode, modifiedBid })}
              >
                {t("aiOperations:runDetail.decisionRecord.sheet.save")}
              </ControlButton>
            ) : null}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function EvidenceDetails({
  action,
}: {
  action: AiOperationsCurrentProposal["artifact"]["actions"][number];
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { formatCurrency, formatPercent } = useFormatters();
  const metrics = action.server.trustedMetrics;
  if (!metrics) {
    return (
      <p className="text-xs leading-5 text-muted-foreground">
        {t("runDetail.decisionRecord.sheet.evidenceUnavailable")}
      </p>
    );
  }
  return (
    <dl className="grid grid-cols-2 gap-x-5 gap-y-2 rounded-[8px] bg-subtle/50 p-3 text-xs">
      <Fact
        label={t("runDetail.decisionRecord.evidence.windowStart")}
        value={metrics.windowStart}
      />
      <Fact label={t("runDetail.decisionRecord.evidence.windowEnd")} value={metrics.windowEnd} />
      <Fact label={t("runDetail.decisionRecord.evidence.clicks")} value={String(metrics.clicks)} />
      <Fact
        label={t("runDetail.decisionRecord.evidence.spend")}
        value={formatCurrency(metrics.spend)}
      />
      <Fact
        label={t("runDetail.decisionRecord.evidence.sales")}
        value={formatCurrency(metrics.sales)}
      />
      <Fact label={t("runDetail.decisionRecord.evidence.orders")} value={String(metrics.orders)} />
      <Fact
        label={t("runDetail.decisionRecord.evidence.acos")}
        value={formatPercent(metrics.acos)}
      />
    </dl>
  );
}

export function HighRiskConfirmationDialog({
  onConfirm,
  onOpenChange,
  open,
  pending,
}: {
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  pending: boolean;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>{t("runDetail.decisionRecord.review.confirmTitle")}</DialogTitle>
          <DialogDescription>
            {t("runDetail.decisionRecord.review.confirmDescription")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" disabled={pending} />}>
            {t("common:actions.cancel")}
          </DialogClose>
          <Button loading={pending} disabled={pending} onClick={onConfirm}>
            {t("runDetail.decisionRecord.review.confirm")}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
